interface ViewerPoint {
  x: number
  y: number
}

interface ViewerState {
  x: number
  y: number
  scale: number
}

interface DragStart extends ViewerPoint {
  stateX: number
  stateY: number
}

interface PinchStart {
  distance: number
  scale: number
  contentX: number
  contentY: number
}

const ZOOM_STEPS = [0.1, 0.25, 0.33, 0.5, 0.67, 0.75, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5, 6]
const MIN_SCALE = ZOOM_STEPS[0]
const MAX_SCALE = 6

const SVG_VIEWER_COLOR_THEMES = ['light', 'blue', 'blue-80', 'green', 'blue-light', 'green-light'] as const
type SvgViewerColorTheme = (typeof SVG_VIEWER_COLOR_THEMES)[number]

const isSvgViewerColorTheme = (value: string): value is SvgViewerColorTheme =>
  (SVG_VIEWER_COLOR_THEMES as readonly string[]).includes(value)

class SvgViewer {
  private overlay: HTMLElement | null = null
  private stage: HTMLElement | null = null
  private image: HTMLImageElement | null = null
  private closeBtn: HTMLButtonElement | null = null
  private zoomInBtn: HTMLButtonElement | null = null
  private zoomOutBtn: HTMLButtonElement | null = null
  private triggers: HTMLElement[] = []
  private lastFocus: HTMLElement | null = null
  private opened = false
  private isLeaving = false
  private pointers = new Map<number, ViewerPoint>()
  private state: ViewerState = { x: 0, y: 0, scale: 1 }
  private dragStart: DragStart | null = null
  private pinchStart: PinchStart | null = null

  init(container: Document | HTMLElement = document): void {
    const triggers = Array.from(container.querySelectorAll<HTMLElement>('[data-hg-svg-viewer-trigger]'))
    if (triggers.length === 0) {
      this.removeBodyOverlays()
      return
    }
    if (!this.ensureOverlay(container)) return

    triggers.forEach((el) => {
      el.addEventListener('click', this.onTriggerClick)
      this.triggers.push(el)
    })
  }

  update(container: Document | HTMLElement = document): void {
    this.detachTriggers()
    this.detachOverlay()
    this.init(container)
  }

  cleanup(): void {
    this.detachTriggers()
    this.detachOverlay()
  }

  private detachTriggers(): void {
    this.triggers.forEach((trigger) => trigger.removeEventListener('click', this.onTriggerClick))
    this.triggers = []
  }

  private detachOverlay(): void {
    this.closeBtn?.removeEventListener('click', this.onCloseClick)
    this.zoomInBtn?.removeEventListener('click', this.onZoomInClick)
    this.zoomOutBtn?.removeEventListener('click', this.onZoomOutClick)
    this.stage?.removeEventListener('pointerdown', this.onPointerDown)
    this.stage?.removeEventListener('pointermove', this.onPointerMove)
    this.stage?.removeEventListener('pointerup', this.onPointerEnd)
    this.stage?.removeEventListener('pointercancel', this.onPointerEnd)
    document.removeEventListener('keydown', this.onKeydown)
    window.removeEventListener('resize', this.onResize)
    document.body.classList.remove('has-svg-viewer-open')
    this.overlay?.classList.remove('is-open', 'is-leaving')
    this.overlay?.setAttribute('aria-hidden', 'true')
    this.resetPointers()
    this.overlay = null
    this.stage = null
    this.image = null
    this.closeBtn = null
    this.zoomInBtn = null
    this.zoomOutBtn = null
    this.opened = false
    this.isLeaving = false
  }

  private ensureOverlay(container: Document | HTMLElement): boolean {
    if (this.overlay?.isConnected) return true

    const overlay = container.querySelector<HTMLElement>('.hg-svg-viewer-modal')
      || document.querySelector<HTMLElement>('.hg-svg-viewer-modal')
    if (!overlay) return false

    const stage = overlay.querySelector<HTMLElement>('.hg-svg-viewer-modal__stage')
    const image = overlay.querySelector<HTMLImageElement>('.hg-svg-viewer-modal__image')
    const closeBtn = overlay.querySelector<HTMLButtonElement>('.hg-svg-viewer-modal__close')
    const zoomInBtn = overlay.querySelector<HTMLButtonElement>('[data-hg-svg-viewer-zoom-in]')
    const zoomOutBtn = overlay.querySelector<HTMLButtonElement>('[data-hg-svg-viewer-zoom-out]')

    if (!stage || !image || !closeBtn) return false

    this.moveOverlayToBody(overlay)
    this.overlay = overlay
    this.stage = stage
    this.image = image
    this.closeBtn = closeBtn
    this.zoomInBtn = zoomInBtn
    this.zoomOutBtn = zoomOutBtn

    this.closeBtn.addEventListener('click', this.onCloseClick)
    this.zoomInBtn?.addEventListener('click', this.onZoomInClick)
    this.zoomOutBtn?.addEventListener('click', this.onZoomOutClick)
    this.stage.addEventListener('pointerdown', this.onPointerDown)
    this.stage.addEventListener('pointermove', this.onPointerMove)
    this.stage.addEventListener('pointerup', this.onPointerEnd)
    this.stage.addEventListener('pointercancel', this.onPointerEnd)
    document.addEventListener('keydown', this.onKeydown)
    window.addEventListener('resize', this.onResize)

    return true
  }

  private syncOverlayThemeClasses(overlay: HTMLElement, theme: SvgViewerColorTheme): void {
    Array.from(overlay.classList).forEach((className) => {
      if (className.startsWith('theme-')) {
        overlay.classList.remove(className)
      }
    })
    overlay.classList.add(`theme-${theme}`)
  }

  private moveOverlayToBody(overlay: HTMLElement): void {
    document.querySelectorAll<HTMLElement>('.hg-svg-viewer-modal').forEach((el) => {
      if (el !== overlay && el.parentElement === document.body) {
        el.parentElement.removeChild(el)
      }
    })

    if (overlay.parentElement !== document.body) {
      document.body.appendChild(overlay)
    }
  }

  private removeBodyOverlays(): void {
    document.querySelectorAll<HTMLElement>('.hg-svg-viewer-modal').forEach((overlay) => {
      if (overlay.parentElement === document.body) {
        overlay.parentElement.removeChild(overlay)
      }
    })
  }

  private onTriggerClick = (e: Event): void => {
    e.preventDefault()

    const trigger = e.currentTarget as HTMLElement
    const viewer = trigger.closest<HTMLElement>('.hg-svg-viewer')
    const source = viewer?.querySelector<HTMLImageElement>('.hg-svg-viewer__image')

    if (!source || !this.image || !this.overlay) return

    const themeRaw = viewer?.dataset.hgSvgViewerTheme?.trim() ?? ''
    const theme: SvgViewerColorTheme = isSvgViewerColorTheme(themeRaw) ? themeRaw : 'light'
    this.syncOverlayThemeClasses(this.overlay, theme)

    this.lastFocus = document.activeElement as HTMLElement | null
    this.image.alt = source.alt || ''
    this.image.removeAttribute('srcset')
    this.image.src = source.currentSrc || source.src
    this.setImageSize(source)

    if (this.image.complete) {
      this.centerImage()
    } else {
      this.image.addEventListener('load', this.onImageLoad, { once: true })
    }

    this.open()
  }

  private setImageSize(source: HTMLImageElement): void {
    if (!this.image) return

    const width = Number(source.getAttribute('width')) || source.naturalWidth
    const height = Number(source.getAttribute('height')) || source.naturalHeight

    this.image.style.width = width > 0 ? `${width}px` : 'auto'
    this.image.style.height = height > 0 ? `${height}px` : 'auto'
  }

  private onImageLoad = (): void => {
    this.centerImage()
  }

  private open(): void {
    if (!this.overlay) return

    this.opened = true
    this.overlay.classList.remove('is-leaving')
    this.overlay.setAttribute('aria-hidden', 'false')
    document.body.classList.add('has-svg-viewer-open')

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (!this.overlay) return
        this.overlay.classList.add('is-open')
        this.closeBtn?.focus()
      })
    })
  }

  private close(): void {
    if (!this.overlay || !this.opened || this.isLeaving) return

    this.isLeaving = true
    this.resetPointers()

    let finished = false
    const finalize = (): void => {
      if (finished || !this.overlay) return
      finished = true
      window.clearTimeout(fallbackTimer)
      this.overlay.removeEventListener('transitionend', onTransitionEnd)
      this.overlay.classList.remove('is-open', 'is-leaving')
      this.overlay.setAttribute('aria-hidden', 'true')
      document.body.classList.remove('has-svg-viewer-open')
      this.opened = false
      this.isLeaving = false

      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus()
      }
    }

    const onTransitionEnd = (e: TransitionEvent): void => {
      if (e.target !== this.overlay || e.propertyName !== 'opacity') return
      finalize()
    }

    this.overlay.addEventListener('transitionend', onTransitionEnd)
    const fallbackTimer = window.setTimeout(finalize, 300)

    requestAnimationFrame(() => {
      this.overlay?.classList.add('is-leaving')
    })
  }

  private centerImage(): void {
    if (!this.stage || !this.image) return

    this.image.style.transform = ''
    const stageRect = this.stage.getBoundingClientRect()
    const stageStyles = window.getComputedStyle(this.stage)
    const paddingLeft = parseFloat(stageStyles.paddingLeft) || 0
    const paddingRight = parseFloat(stageStyles.paddingRight) || 0
    const paddingTop = parseFloat(stageStyles.paddingTop) || 0
    const paddingBottom = parseFloat(stageStyles.paddingBottom) || 0
    const availableWidth = Math.max(stageRect.width - paddingLeft - paddingRight, 1)
    const availableHeight = Math.max(stageRect.height - paddingTop - paddingBottom, 1)
    const imageRect = this.image.getBoundingClientRect()
    const imageWidth = imageRect.width || this.image.naturalWidth
    const imageHeight = imageRect.height || this.image.naturalHeight
    const containScale = Math.min(availableWidth / imageWidth, availableHeight / imageHeight, 1)

    this.state = {
      x: paddingLeft + (availableWidth - imageWidth * containScale) / 2,
      y: paddingTop + (availableHeight - imageHeight * containScale) / 2,
      scale: containScale,
    }
    this.applyTransform()
  }

  private applyTransform(): void {
    if (!this.image) return

    this.image.style.transform = `translate3d(${this.state.x}px, ${this.state.y}px, 0) scale(${this.state.scale})`
  }

  private zoomToStep(direction: 1 | -1): void {
    if (!this.stage) return

    const current = this.state.scale
    const target = direction > 0
      ? ZOOM_STEPS.find((step) => step > current + 0.001) ?? MAX_SCALE
      : [...ZOOM_STEPS].reverse().find((step) => step < current - 0.001) ?? MIN_SCALE

    const rect = this.stage.getBoundingClientRect()
    this.zoomToScale(target, {
      x: rect.width / 2,
      y: rect.height / 2,
    })
  }

  private zoomToScale(scale: number, anchor: ViewerPoint): void {
    const nextScale = this.clamp(scale, MIN_SCALE, MAX_SCALE)
    const contentX = (anchor.x - this.state.x) / this.state.scale
    const contentY = (anchor.y - this.state.y) / this.state.scale

    this.state.scale = nextScale
    this.state.x = anchor.x - contentX * nextScale
    this.state.y = anchor.y - contentY * nextScale
    this.applyTransform()
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (!this.stage || !this.opened) return

    e.preventDefault()
    this.stage.setPointerCapture(e.pointerId)
    this.stage.classList.add('is-dragging')
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })

    if (this.pointers.size === 1) {
      this.dragStart = {
        x: e.clientX,
        y: e.clientY,
        stateX: this.state.x,
        stateY: this.state.y,
      }
      this.pinchStart = null
    } else if (this.pointers.size === 2) {
      this.dragStart = null
      this.pinchStart = this.getPinchStart()
    }
  }

  private onPointerMove = (e: PointerEvent): void => {
    if (!this.opened || !this.pointers.has(e.pointerId)) return

    e.preventDefault()
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY })

    if (this.pointers.size === 2 && this.pinchStart) {
      this.updatePinch()
      return
    }

    if (this.pointers.size === 1 && this.dragStart) {
      this.state.x = this.dragStart.stateX + e.clientX - this.dragStart.x
      this.state.y = this.dragStart.stateY + e.clientY - this.dragStart.y
      this.applyTransform()
    }
  }

  private onPointerEnd = (e: PointerEvent): void => {
    if (!this.stage) return

    if (this.stage.hasPointerCapture(e.pointerId)) {
      this.stage.releasePointerCapture(e.pointerId)
    }

    this.pointers.delete(e.pointerId)
    this.pinchStart = null

    if (this.pointers.size === 0) {
      this.dragStart = null
      this.stage.classList.remove('is-dragging')
      return
    }

    const remaining = Array.from(this.pointers.values())[0]
    this.dragStart = {
      x: remaining.x,
      y: remaining.y,
      stateX: this.state.x,
      stateY: this.state.y,
    }
  }

  private getPinchStart(): PinchStart | null {
    const points = Array.from(this.pointers.values())
    if (points.length < 2) return null

    const center = this.getStagePoint(this.getCenter(points[0], points[1]))
    return {
      distance: Math.max(this.getDistance(points[0], points[1]), 1),
      scale: this.state.scale,
      contentX: (center.x - this.state.x) / this.state.scale,
      contentY: (center.y - this.state.y) / this.state.scale,
    }
  }

  private updatePinch(): void {
    if (!this.pinchStart) return

    const points = Array.from(this.pointers.values())
    if (points.length < 2) return

    const center = this.getStagePoint(this.getCenter(points[0], points[1]))
    const distance = this.getDistance(points[0], points[1])
    this.zoomToScale(this.pinchStart.scale * (distance / this.pinchStart.distance), center)
  }

  private getStagePoint(point: ViewerPoint): ViewerPoint {
    if (!this.stage) return point

    const rect = this.stage.getBoundingClientRect()
    return {
      x: point.x - rect.left,
      y: point.y - rect.top,
    }
  }

  private getCenter(a: ViewerPoint, b: ViewerPoint): ViewerPoint {
    return {
      x: (a.x + b.x) / 2,
      y: (a.y + b.y) / 2,
    }
  }

  private getDistance(a: ViewerPoint, b: ViewerPoint): number {
    return Math.hypot(a.x - b.x, a.y - b.y)
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(Math.max(value, min), max)
  }

  private resetPointers(): void {
    this.pointers.clear()
    this.dragStart = null
    this.pinchStart = null
    this.stage?.classList.remove('is-dragging')
  }

  private onCloseClick = (): void => this.close()

  private onZoomInClick = (): void => this.zoomToStep(1)

  private onZoomOutClick = (): void => this.zoomToStep(-1)

  private onResize = (): void => {
    if (this.opened) this.centerImage()
  }

  private onKeydown = (e: KeyboardEvent): void => {
    if (!this.opened) return

    if (e.key === 'Escape') {
      this.close()
      return
    }

    if (e.key === 'Tab' && this.overlay) {
      const focusables = Array.from(
        this.overlay.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null)

      if (focusables.length === 0) return

      const first = focusables[0]
      const last = focusables[focusables.length - 1]

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault()
        last.focus()
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }
}

export default new SvgViewer()
